import React from 'react';

import { Badge, Icon } from '../../../components/common';
import { useAppStore } from '../../../store/useAppStore';
import type { Prop, PropStatus } from '../../props/types';
import { deriveDefaultRehearsalCharacterIds } from '../../show-structure/utils/rehearsal-script';
import { DashboardWidgetCard } from './DashboardWidgetCard';

const STATUS_VARIANTS: Record<PropStatus, 'secondary' | 'success' | 'warning' | 'purple' | 'teal'> = {
    undefined: 'secondary',
    chosen: 'teal',
    to_build: 'warning',
    to_purchase: 'warning',
    rehearsal: 'purple',
    performance: 'success',
};

function formatStatus(status: PropStatus): string {
    if (status === 'undefined') {
        return 'Unassigned';
    }
    return status
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function getPropStatus(prop: Prop): PropStatus {
    return prop.status ?? 'undefined';
}

function getPropsForCharacters(props: Prop[], characterIds: string[]): Prop[] {
    const characterIdSet = new Set(characterIds);
    return props
        .filter(prop => !prop.deleted && prop.assignedCharacterId && characterIdSet.has(prop.assignedCharacterId))
        .toSorted((left, right) => {
            const statusOrder = getPropStatus(left).localeCompare(getPropStatus(right));
            return statusOrder === 0 ? left.name.localeCompare(right.name) : statusOrder;
        });
}

export const MyPropsChecklistWidget: React.FC = () => {
    const personnelId = useAppStore(state => state.cloud.member?.personnelId ?? null);
    const assignments = useAppStore(state => state.castAssignments || []);
    const characters = useAppStore(state => state.showStructure.characters || []);
    const props = useAppStore(state => state.props || []);

    const selectedCharacterIds = deriveDefaultRehearsalCharacterIds({
        personnelId,
        assignments,
        characters,
    });
    const myProps = getPropsForCharacters(props, selectedCharacterIds);

    return (
        <DashboardWidgetCard
            title="My Props"
            icon="inventory_2"
            footer={myProps.length > 0 ? <Badge variant="secondary" size="sm">{myProps.length}</Badge> : null}
            testId="my-props-checklist-widget"
        >
            {!personnelId ? (
                <div className="text-center py-6">
                    <Icon name="person_search" size="xl" className="text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No linked cast profile</p>
                </div>
            ) : myProps.length === 0 ? (
                <div className="text-center py-6">
                    <Icon name="inventory" size="xl" className="text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No assigned props</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {myProps.slice(0, 4).map(prop => {
                        const status = getPropStatus(prop);

                        return (
                            <div
                                key={prop.id}
                                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-3"
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <p className="min-w-0 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {prop.name}
                                    </p>
                                    <Badge variant={STATUS_VARIANTS[status]} size="sm">
                                        {formatStatus(status)}
                                    </Badge>
                                </div>
                                {prop.entrance || prop.exit ? (
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        {[prop.entrance, prop.exit].filter(Boolean).join(' / ')}
                                    </p>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}
        </DashboardWidgetCard>
    );
};
